// src/App.jsx
import React from "react";
import {
	CssBaseline,
	Box,
	Grid,
	Paper,
	Typography,
	Button,
	Card,
	CardContent,
	Stack,
} from "@mui/material";

import {
	BarChart as BarChartIcon,
	ShowChart as ShowChartIcon,
	Timeline as TimelineIcon,
	Assessment as AssessmentIcon,
} from "@mui/icons-material";

import WeeklyShipmentsChart from "./WeeklyShipmentsChart";

const Analytics = () => {
	// Dummy stats data
	const stats = [
		{
			title: "Total Shipments",
			value: "1,245",
			icon: <BarChartIcon fontSize="large" />,
		},
		{
			title: "Active Drivers",
			value: "85",
			icon: <TimelineIcon fontSize="large" />,
		},
		{
			title: "Today's Revenue",
			value: "$12,340",
			icon: <ShowChartIcon fontSize="large" />,
		},
		{
			title: "Pending Orders",
			value: "45",
			icon: <AssessmentIcon fontSize="large" />,
		},
	];

	// Dummy recent activities
	const activities = [
		{ desc: "Shipment #1234 delivered to John Doe", time: "2 mins ago" },
		{ desc: "New driver onboarded: Mike Ross", time: "1 hour ago" },
		{ desc: "Order #5678 pending pickup", time: "3 hours ago" },
		{ desc: "Weekly revenue report generated", time: "1 day ago" },
	];

	return (
		<Box
			sx={{
				p: 4,
				bgcolor: "background.default",
				fontFamily: "Arial, sans-serif",
			}}>
			<CssBaseline />

			{/* Animated Header */}
			<Typography
				variant="h3"
				gutterBottom
				sx={{
					mb: 4,
					fontWeight: "bold",
					textAlign: "center",
					color: "primary.main",
					animation: "slideDown 1s ease-out",
				}}>
				Analytics Dashboard
			</Typography>

			{/* Stats Cards */}
			<Grid container spacing={3} sx={{ mb: 4 }}>
				{stats.map((stat, index) => (
					<Grid item xs={12} sm={6} md={3} key={index}>
						<Card
							sx={{
								p: 2,
								bgcolor: "white",
								borderRadius: 2,
								boxShadow: 4,
								transition: "all 0.3s",
								"&:hover": { boxShadow: 8, transform: "translateY(-4px)" },
								height: "100%",
							}}>
							<Stack direction="row" alignItems="center" spacing={2}>
								{stat.icon}
								<Box>
									<Typography variant="subtitle2" color="text.secondary">
										{stat.title}
									</Typography>
									<Typography
										variant="h4"
										fontWeight="bold"
										color="primary.main">
										{stat.value}
									</Typography>
								</Box>
							</Stack>
						</Card>
					</Grid>
				))}
			</Grid>

			{/* Main Analytics & Activities */}
			<Grid container spacing={3}>
				{/* Recent Activities */}
				<Grid item xs={12} md={6}>
					<Paper
						sx={{
							p: 3,
							borderRadius: 2,
							boxShadow: 4,
							transition: "all 0.3s",
							"&:hover": { boxShadow: 8, transform: "translateY(-2px)" },
						}}>
						<Typography
							variant="h6"
							gutterBottom
							sx={{ mb: 2, fontWeight: "bold" }}>
							Recent Activities
						</Typography>
						{activities.map((activity, idx) => (
							<Box
								key={idx}
								sx={{
									mb: 2,
									p: 2,
									borderRadius: 1,
									bgcolor: "grey.100",
									transition: "all 0.2s",
									"&:hover": { bgcolor: "grey.200" },
								}}>
								<Typography variant="body1">{activity.desc}</Typography>
								<Typography variant="caption" color="text.secondary">
									{activity.time}
								</Typography>
							</Box>
						))}
					</Paper>
				</Grid>

				{/* Charts & Insights */}
				<Grid item xs={12} md={6}>
					<Paper
						sx={{
							p: 3,
							borderRadius: 2,
							boxShadow: 4,
							display: "flex",
							flexDirection: "column",
							transition: "all 0.3s",
							"&:hover": { boxShadow: 8, transform: "translateY(-2px)" },
						}}>
						<Typography
							variant="h6"
							gutterBottom
							sx={{ mb: 2, fontWeight: "bold" }}>
							Weekly Shipments Trend
						</Typography>
						<WeeklyShipmentsChart />
					</Paper>
				</Grid>
			</Grid>

			{/* Call to Action Button */}
			<Box sx={{ my: 6, textAlign: "center" }}>
				<Button
					variant="contained"
					color="primary"
					sx={{
						px: 4,
						py: 1.8,
						fontSize: "1.2rem",
						borderRadius: 2,
						transition: "transform 0.3s",
						"&:hover": { transform: "scale(1.05)" },
					}}>
					View Full Reports
				</Button>
			</Box>

			{/* Keyframes for slideDown animation */}
			<style>
				{`
          @keyframes slideDown {
            from { opacity: 0; transform: translateY(-20px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}
			</style>
		</Box>
	);
};

export default Analytics;
